import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './StatusBadge';
import { PriorityIndicator } from './PriorityIndicator';
import { ProgressBar } from './ProgressBar';
import { AssigneeList } from './AssigneeList';
import type { Audit } from '@/types/asset-audit';
import { AuditType } from '@/types/asset-audit';
import { cn } from '@/lib/utils';
import { Calendar, MapPin, Clock, MoreVertical, Pencil, Copy, Trash2, Eye } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';

interface AuditCardProps {
  readonly audit: Audit;
  readonly onClick?: () => void;
  readonly className?: string;
  readonly onEdit?: (auditId: string) => void;
  readonly onDuplicate?: (auditId: string) => void;
  readonly onDelete?: (auditId: string) => void;
}

const auditTypeConfig: Record<AuditType, { label: string; color: string }> = {
  [AuditType.FullInventory]: { label: 'Full Inventory', color: 'text-blue-600' },
  [AuditType.LocationVerification]: { label: 'Location Verification', color: 'text-indigo-600' },
  [AuditType.ConditionAssessment]: { label: 'Condition Assessment', color: 'text-amber-600' },
  [AuditType.ComplianceCheck]: { label: 'Compliance Check', color: 'text-purple-600' },
  [AuditType.HighValueAssets]: { label: 'High Value Assets', color: 'text-green-600' },
  [AuditType.Reconciliation]: { label: 'Reconciliation', color: 'text-orange-600' },
  [AuditType.SpotCheck]: { label: 'Spot Check', color: 'text-cyan-600' },
  [AuditType.Physical]: { label: 'Physical', color: 'text-blue-500' },
  [AuditType.Compliance]: { label: 'Compliance', color: 'text-purple-500' },
  [AuditType.Financial]: { label: 'Financial', color: 'text-green-500' },
  [AuditType.Condition]: { label: 'Condition', color: 'text-amber-500' },
  [AuditType.Verification]: { label: 'Verification', color: 'text-indigo-500' },
};

export function AuditCard({ audit, onClick, className, onEdit, onDuplicate, onDelete }: Readonly<AuditCardProps>) {
  const typeConfig = auditTypeConfig[audit.type];
  const isOverdue = audit.scheduledEndDate && new Date(audit.scheduledEndDate) < new Date();

  const handleMenuAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <Card
      className={cn(
        'cursor-pointer hover:shadow-md transition-shadow',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Type & Priority & Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn('text-xs font-medium', typeConfig.color)}>
              {typeConfig.label}
            </span>
            <StatusBadge status={audit.status} />
          </div>
          <PriorityIndicator priority={audit.priority} size="sm" showLabel />
        </div>

        {/* Title & Number */}
        <div>
          <div className="font-medium text-sm line-clamp-2 text-primary">{audit.title}</div>
          <div className="text-xs text-muted-foreground mt-1">#{audit.auditNumber}</div>
        </div>

        {/* Progress */}
        {audit.totalAssets > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">
              {audit.completedAssets} of {audit.totalAssets} assets
            </div>
            <ProgressBar
              current={audit.completedAssets}
              total={audit.totalAssets}
              showPercentage
              size="sm"
            />
          </div>
        )}

        {/* Metadata */}
        <div className="space-y-2 text-xs">
          {/* Location */}
          {audit.scopeDetails.locations && audit.scopeDetails.locations.length > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{audit.scopeDetails.locations[0]}</span>
            </div>
          )}

          {/* Start Date */}
          {audit.scheduledStartDate && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-3 w-3 flex-shrink-0" />
              <span>{format(new Date(audit.scheduledStartDate), 'MMM d, yyyy')}</span>
            </div>
          )}

          {/* Due Date */}
          {audit.scheduledEndDate && (
            <div
              className={cn(
                'flex items-center gap-2',
                isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
              )}
            >
              <Clock className="h-3 w-3 flex-shrink-0" />
              <span>
                {isOverdue && 'Overdue: '}
                {format(new Date(audit.scheduledEndDate), 'MMM d, yyyy')}
              </span>
            </div>
          )}
        </div>

        {/* Assignees & Actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          {audit.assignedUsers && audit.assignedUsers.length > 0 ? (
            <AssigneeList assignees={audit.assignedUsers} maxVisible={3} size="xs" />
          ) : (
            <div className="flex-1" />
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0">
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => handleMenuAction(e, () => onClick?.())}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={(e) => handleMenuAction(e, () => onEdit(audit.id))}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {onDuplicate && (
                <DropdownMenuItem onClick={(e) => handleMenuAction(e, () => onDuplicate(audit.id))}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => handleMenuAction(e, () => onDelete(audit.id))}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}
